import React, { useEffect, useState, useRef, memo, Component } from 'react';
import { Wrench, ClipboardCheck, Calculator, Map, AlertTriangle, CheckCircle2, ArrowRight, ShieldAlert, Droplets, Hammer, FileText, Search, ChevronDown, ChevronLeft, ChevronRight, Plus, Minus, XCircle, Clock, X, ShoppingBag, Star, ThumbsUp, MessageSquare, Maximize2, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
// --- Types ---
interface SectionProps {
  className?: string;
  children: React.ReactNode;
}
// --- Components ---
const Button = ({
  children,
  className = '',
  variant = 'primary',
  onClick
}: {
  children: React.ReactNode;
  className?: string;
  variant?: 'primary' | 'secondary';
  onClick?: () => void;
}) => {
  const baseStyles = 'px-8 py-4 rounded-md font-bold text-lg transition-all duration-300 transform hover:scale-105 active:scale-95 text-center inline-block';
  const variants = {
    primary: 'bg-[#22c55e] hover:bg-[#16a34a] text-white shadow-lg shadow-green-900/20',
    secondary: 'bg-white text-[#0f172a] border-2 border-[#0f172a] hover:bg-gray-50'
  };
  return <button onClick={onClick} className={`${baseStyles} ${variants[variant]} ${className}`} data-id="element-3">
      {children}
    </button>;
};
const Section = ({
  children,
  className = ''
}: SectionProps) => <section className={`py-16 md:py-24 px-6 md:px-12 max-w-7xl mx-auto ${className}`} data-id="element-4">
    {children}
  </section>;
const Badge = ({
  children
}: {
  children: React.ReactNode;
}) => <span className="inline-block px-3 py-1 bg-blue-100 text-blue-800 text-xs font-bold uppercase tracking-wider rounded mb-4" data-id="element-5">
    {children}
  </span>;
const BonusLabel = () => <div className="inline-block px-4 py-2 bg-yellow-400 text-slate-900 font-black uppercase tracking-tighter rounded-md shadow-lg border-2 border-yellow-500 transform -rotate-1 pt-[6px] pb-[6px] pl-[6px] pr-[6px] text-[12px]" data-id="element-6">
    TE LO LLEVAS GRATIS HOY
  </div>;
const FAQItem = ({
  question,
  answer
}: {
  question: string;
  answer: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border-b border-gray-200 py-4" data-id="element-7">
      <button onClick={() => setIsOpen(!isOpen)} className="flex justify-between items-center w-full text-left font-bold text-slate-800 hover:text-blue-700 transition-colors" data-id="element-8">
        <span data-id="element-9">{question}</span>
        {isOpen ? <Minus className="w-5 h-5" data-id="element-10" /> : <Plus className="w-5 h-5" data-id="element-11" />}
      </button>
      {isOpen && <div className="mt-4 text-slate-600 leading-relaxed" data-id="element-12">{answer}</div>}
    </div>;
};
const ReviewCard = ({
  name,
  location,
  stars,
  date,
  text,
  verified,
  isDesignExample = false
}: {
  name: string;
  location?: string;
  stars: number;
  date: string;
  text: string;
  verified: boolean;
  isDesignExample?: boolean;
}) => {
  const initials = name.split(' ').map(n => n[0]).join('').toUpperCase();
  return <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 flex flex-col h-full relative overflow-hidden" data-id="element-13">
      <div className="flex items-center gap-3 mb-4" data-id="element-14">
        <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold text-sm shrink-0" data-id="element-15">
          {initials}
        </div>
        <div className="min-w-0" data-id="element-16">
          <h4 className="font-bold text-slate-900 text-sm truncate" data-id="element-17">{name}</h4>
          {location && <p className="text-slate-400 text-[10px] font-medium truncate uppercase tracking-wider" data-id="element-18">
              {location}
            </p>}
        </div>
      </div>

      <div className="flex items-center gap-1 mb-3" data-id="element-19">
        {[...Array(5)].map((_, i) => <Star key={i} className={`w-3.5 h-3.5 ${i < stars ? 'fill-yellow-400 text-yellow-400' : 'text-slate-200'}`} data-id="element-20" />)}
        <span className="text-slate-300 text-[10px] ml-2 font-medium" data-id="element-21">
          {date}
        </span>
      </div>

      <p className="text-slate-600 text-sm leading-relaxed mb-6 flex-grow italic" data-id="element-22">
        "{text}"
      </p>

      <div className="flex items-center justify-between mt-auto pt-4 border-t border-slate-50" data-id="element-23">
        {verified && <div className="flex items-center gap-1 text-green-600 text-[10px] font-bold uppercase tracking-tight" data-id="element-24">
            <CheckCircle2 className="w-3 h-3" data-id="element-25" />
            Compra verificada
          </div>}
        <div className="flex items-center gap-3" data-id="element-26">
          <button className="text-slate-400 hover:text-blue-600 transition-colors flex items-center gap-1 text-[10px] font-bold" data-id="element-27">
            <ThumbsUp className="w-3 h-3" data-id="element-28" />
            Útil
          </button>
          <button className="text-slate-400 hover:text-blue-600 transition-colors flex items-center gap-1 text-[10px] font-bold" data-id="element-29">
            <MessageSquare className="w-3 h-3" data-id="element-30" />
            Responder
          </button>
        </div>
      </div>
    </div>;
};
const ReviewForm = () => {
  const [submitted, setSubmitted] = useState(false);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  if (submitted) {
    return <div className="bg-green-50 border-2 border-green-100 p-8 rounded-3xl text-center" data-id="element-31">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-32">
          <CheckCircle2 className="w-8 h-8 text-green-600" data-id="element-33" />
        </div>
        <h3 className="text-xl font-black text-slate-900 mb-2" data-id="element-34">
          ¡Gracias por compartir!
        </h3>
        <p className="text-slate-600 font-medium" data-id="element-35">
          Tu comentario será revisado antes de publicarse.
        </p>
      </div>;
  }
  return <div className="bg-slate-50 p-8 md:p-12 rounded-3xl border border-slate-200" data-id="element-36">
      <h3 className="text-2xl font-black text-slate-900 mb-8 text-center uppercase tracking-tight" data-id="element-37">
        COMPARTE TU EXPERIENCIA
      </h3>
      <form onSubmit={e => {
      e.preventDefault();
      setSubmitted(true);
    }} className="space-y-6 max-w-2xl mx-auto" data-id="element-38">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-39">
          <div className="space-y-2" data-id="element-40">
            <label className="text-xs font-black text-slate-500 uppercase tracking-widest ml-1" data-id="element-41">
              Nombre
            </label>
            <input required type="text" className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-blue-500 outline-none transition-all" placeholder="Tu nombre" data-id="element-42" />
          </div>
          <div className="space-y-2" data-id="element-43">
            <label className="text-xs font-black text-slate-500 uppercase tracking-widest ml-1" data-id="element-44">
              Ciudad / Provincia (Opcional)
            </label>
            <input type="text" className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-blue-500 outline-none transition-all" placeholder="Ej: Córdoba" data-id="element-45" />
          </div>
        </div>

        <div className="space-y-2" data-id="element-46">
          <label className="text-xs font-black text-slate-500 uppercase tracking-widest ml-1" data-id="element-47">
            Correo electrónico (No será público)
          </label>
          <input required type="email" className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-blue-500 outline-none transition-all" placeholder="tu@email.com" data-id="element-48" />
        </div>

        <div className="space-y-2" data-id="element-49">
          <label className="text-xs font-black text-slate-500 uppercase tracking-widest ml-1" data-id="element-50">
            Calificación
          </label>
          <div className="flex items-center gap-2 bg-white p-3 rounded-xl border border-slate-200 w-fit" data-id="element-51">
            {[...Array(5)].map((_, i) => {
            const starValue = i + 1;
            return <button key={i} type="button" onClick={() => setRating(starValue)} onMouseEnter={() => setHover(starValue)} onMouseLeave={() => setHover(0)} className="focus:outline-none transition-transform hover:scale-110" data-id="element-52">
                  <Star className={`w-6 h-6 ${starValue <= (hover || rating) ? 'fill-yellow-400 text-yellow-400' : 'text-slate-200'}`} data-id="element-53" />
                </button>;
          })}
          </div>
        </div>

        <div className="space-y-2" data-id="element-54">
          <label className="text-xs font-black text-slate-500 uppercase tracking-widest ml-1" data-id="element-55">
            Tu comentario
          </label>
          <textarea required rows={4} className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-blue-500 outline-none transition-all resize-none" placeholder="Cuéntanos qué te pareció el material..." data-id="element-56" />
        </div>

        <div className="flex items-start gap-3" data-id="element-57">
          <input required type="checkbox" className="mt-1 w-4 h-4 rounded" data-id="element-58" />
          <p className="text-[10px] text-slate-500 font-medium leading-relaxed" data-id="element-59">
            Doy mi consentimiento para que mi nombre y comentario sean
            publicados en este sitio web tras ser revisados.
          </p>
        </div>

        <button type="submit" className="w-full bg-[#0f172a] text-white font-black py-4 rounded-xl hover:bg-blue-900 transition-all shadow-lg shadow-blue-900/10 uppercase tracking-widest" data-id="element-60">
          ENVIAR MI OPINIÓN
        </button>
      </form>
    </div>;
};
const ReviewsSection = () => {
  const [showAll, setShowAll] = useState(false);
  // --- REAL REVIEWS (Placeholder for design mode) ---
  const designReviews = [{
    name: 'Facundo G.',
    location: 'Córdoba',
    stars: 5,
    date: '15/06/2026',
    text: 'Excelente material, muy claro y práctico. El Método I.D.E.A. me cambió la forma de encarar los arreglos.',
    verified: true
  }, {
    name: 'Santiago M.',
    location: 'Buenos Aires',
    stars: 5,
    date: '12/06/2026',
    text: 'Me sirvió mucho para organizar mis presupuestos. El bono de gestión profesional es oro puro.',
    verified: true
  }, {
    name: 'Valentina R.',
    location: 'Mendoza',
    stars: 4,
    date: '08/06/2026',
    text: 'Muy buena la guía de diagnóstico. Algunos diagramas técnicos son un poco densos pero muy completos.',
    verified: true
  }, {
    name: 'Mateo P.',
    location: 'Rosario',
    stars: 5,
    date: '05/06/2026',
    text: 'Lo recomiendo para quienes están empezando. Te ahorra mucho tiempo de prueba y error.',
    verified: true
  }, {
    name: 'Lucía B.',
    location: 'Neuquén',
    stars: 5,
    date: '01/06/2026',
    text: 'Las planillas de gestión son excelentes. Ahora mis clientes me ven mucho más profesional.',
    verified: true
  }];
  return <div className="py-24 bg-white" data-id="element-61">
      <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-62">
        <div className="text-center mb-16" data-id="element-63">
          <Badge data-id="element-64">APROBACIÓN SOCIAL</Badge>
          <h2 className="text-3xl md:text-5xl font-black text-slate-900 mb-4 uppercase tracking-tight" data-id="element-65">
            LO QUE DICEN QUIENES YA ADQUIRIERON EL OFICIO DE LA PLOMERÍA
          </h2>
          <p className="text-xl text-slate-500 font-medium" data-id="element-66">
            Experiencias compartidas por compradores reales de la colección.
          </p>
        </div>

        {/* Grid: 3 top, 2 bottom on desktop */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12" data-id="element-67">
          {designReviews.slice(0, 3).map((review, i) => <ReviewCard key={i} {...review} isDesignExample={true} data-id="element-68" />)}
          <div className="lg:col-span-3 grid grid-cols-1 md:grid-cols-2 gap-6 lg:max-w-4xl lg:mx-auto w-full" data-id="element-69">
            {designReviews.slice(3, 5).map((review, i) => <ReviewCard key={i + 3} {...review} isDesignExample={true} data-id="element-70" />)}
          </div>
        </div>

        <div className="flex justify-center mb-24" data-id="element-71">
          <button onClick={() => setShowAll(true)} className="flex items-center gap-2 text-blue-600 font-black uppercase tracking-widest text-sm hover:text-blue-800 transition-colors group" data-id="element-72">
            VER MÁS OPINIONES
            <ChevronDown className="w-4 h-4 group-hover:translate-y-1 transition-transform" data-id="element-73" />
          </button>
        </div>

        <ReviewForm data-id="element-74" />
      </div>
    </div>;
};
const InsideContentSection = ({
  onAction
}: {
  onAction: () => void;
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const images = [{
    title: 'Fundamentos del oficio y seguridad',
    url: 'https://cdn.magicpatterns.com/uploads/uBmPDN2uRkNftLM6LyH3jm/Captura_de_pantalla_2026-07-01_165227.png'
  }, {
    title: 'Interpretación de planos de plomería',
    url: 'https://cdn.magicpatterns.com/uploads/dybZSCCVp8tsWvCubAHGL3/Captura_de_pantalla_2026-07-01_165237.png'
  }, {
    title: 'Materiales y tipos de tuberías',
    url: 'https://cdn.magicpatterns.com/uploads/vkMfzsT2Nciwr5KzUVnjcP/Captura_de_pantalla_2026-07-01_165246.png'
  }, {
    title: 'Conexiones, cortes y técnicas de unión',
    url: 'https://cdn.magicpatterns.com/uploads/s93vzJiV5AQbFutuAYDtd4/Captura_de_pantalla_2026-07-01_165254.png'
  }, {
    title: 'Sistemas de abastecimiento de agua',
    url: 'https://cdn.magicpatterns.com/uploads/fDh22xPAGxXEmrku3yv6G6/Captura_de_pantalla_2026-07-01_165303.png'
  }, {
    title: 'Bombas, cisternas y tinacos',
    url: 'https://cdn.magicpatterns.com/uploads/cE5ekC2KLC85XpPZ1xzNNj/Captura_de_pantalla_2026-07-01_165311.png'
  }, {
    title: 'Instalaciones sanitarias y drenajes',
    url: 'https://cdn.magicpatterns.com/uploads/7TaTL6SLdwtyvF3phE7irq/Captura_de_pantalla_2026-07-01_165338.png'
  }, {
    title: 'Instalación de muebles y servicios',
    url: 'https://cdn.magicpatterns.com/uploads/sTEDGFXxvdEJmEnbkf6SxG/Captura_de_pantalla_2026-07-01_165435.png'
  }, {
    title: 'Cálculos de presión, caudal y pendientes',
    url: 'https://cdn.magicpatterns.com/uploads/ovnHMTxQzPwGRQriDn1haG/Captura_de_pantalla_2026-07-01_165448.png'
  }, {
    title: 'Diagnóstico, mantenimiento y soluciones',
    url: 'https://cdn.magicpatterns.com/uploads/kRVFxYfkTGLU6hpSYV1oEu/Captura_de_pantalla_2026-07-01_165500.png'
  }];
  const next = () => setCurrentIndex(prev => (prev + 1) % images.length);
  const prev = () => setCurrentIndex(prev => (prev - 1 + images.length) % images.length);
  return <section className="py-24 bg-slate-50 overflow-hidden" data-id="element-75">
      <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-76">
        <div className="text-center mb-16" data-id="element-77">
          <h2 className="text-3xl md:text-5xl font-black text-[#0f172a] mb-4 uppercase tracking-tight" data-id="element-78">
            DESCUBRE TODO LO QUE ENCONTRARÁS ADENTRO
          </h2>
          <p className="text-xl text-slate-600 font-medium max-w-4xl mx-auto" data-id="element-79">
            Explora una muestra del contenido, los procedimientos, las
            ilustraciones, los cálculos y los recursos prácticos incluidos en El
            Oficio de la Plomería.
          </p>
        </div>

        {/* Carousel */}
        <div className="relative max-w-4xl mx-auto mb-20" data-id="element-80">
          <div className="relative aspect-[4/3] md:aspect-[16/10] bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden group" data-id="element-81">
            <AnimatePresence mode="wait" data-id="element-82">
              <motion.div key={currentIndex} initial={{
              opacity: 0,
              x: 50
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -50
            }} transition={{
              duration: 0.3
            }} drag="x" dragConstraints={{
              left: 0,
              right: 0
            }} dragElastic={0.2} onDragEnd={(_, info) => {
              if (info.offset.x > 50) prev();else if (info.offset.x < -50) next();
            }} className="absolute inset-0 cursor-zoom-in touch-pan-y" onClick={() => setIsLightboxOpen(true)} data-id="element-83">
                <img src={images[currentIndex].url} alt={images[currentIndex].title} className="w-full h-full object-contain p-4 md:p-8" data-id="element-84" />
                <div className="absolute top-4 right-4 bg-[#0f172a]/80 text-white px-3 py-1 rounded-full text-xs font-bold backdrop-blur-sm" data-id="element-85">
                  {currentIndex + 1} de {images.length}
                </div>
                <div className="absolute bottom-4 right-4 bg-white/90 p-2 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-86">
                  <Maximize2 className="w-5 h-5 text-[#0f172a]" data-id="element-87" />
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Arrows */}
            <button onClick={prev} className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-3 rounded-full shadow-lg text-[#0f172a] transition-all z-10" data-id="element-88">
              <ChevronLeft className="w-6 h-6" data-id="element-89" />
            </button>
            <button onClick={next} className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-3 rounded-full shadow-lg text-[#0f172a] transition-all z-10" data-id="element-90">
              <ChevronRight className="w-6 h-6" data-id="element-91" />
            </button>
          </div>

          {/* Caption */}
          <div className="mt-6 text-center" data-id="element-92">
            <h3 className="text-xl font-black text-[#0f172a] mb-2" data-id="element-93">
              {images[currentIndex].title}
            </h3>
            <div className="flex justify-center gap-2 mt-4" data-id="element-94">
              {images.map((_, i) => <button key={i} onClick={() => setCurrentIndex(i)} className={`w-2.5 h-2.5 rounded-full transition-all ${i === currentIndex ? 'bg-blue-600 w-6' : 'bg-slate-300'}`} data-id="element-95" />)}
            </div>
          </div>
        </div>

        {/* Content Block */}
        <div className="text-center mb-20" data-id="element-96">
          <h2 className="text-2xl md:text-3xl font-black text-[#0f172a] mb-8 uppercase tracking-tight" data-id="element-97">
            NO ES SOLO INFORMACIÓN: ES UNA GUÍA PARA CONSULTAR Y APLICAR
          </h2>
          <div className="space-y-6 text-lg text-slate-600 max-w-4xl mx-auto leading-relaxed" data-id="element-98">
            <p data-id="element-99">
              Dentro de El Oficio de la Plomería encontrarás contenido
              organizado para ayudarte a comprender cómo funciona cada sistema,
              reconocer materiales, interpretar instalaciones y seguir
              procedimientos con mayor claridad.
            </p>
            <p data-id="element-100">
              Además del contenido escrito, tendrás acceso a recursos visuales y
              videos prácticos disponibles dentro del material, donde podrás
              observar procedimientos, herramientas, conexiones y ejemplos de
              aplicación.
            </p>
            <p data-id="element-101">
              Todo el contenido está organizado para que puedas avanzar desde
              los fundamentos del oficio hasta temas técnicos como presión,
              caudal, pendientes, bombas, drenajes, instalaciones hidráulicas,
              sistemas sanitarios y mantenimiento.
            </p>
          </div>
        </div>

        {/* Benefit Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-20" data-id="element-102">
          {[{
          title: 'Contenido paso a paso',
          text: 'Explicaciones ordenadas para comprender cada tema desde sus fundamentos hasta su aplicación práctica.'
        }, {
          title: 'Videos prácticos',
          text: 'Material audiovisual para observar técnicas, herramientas, conexiones y procedimientos incluidos en el programa.'
        }, {
          title: 'Imágenes y diagramas',
          text: 'Recursos visuales para reconocer piezas, interpretar recorridos y comprender mejor cada instalación.'
        }, {
          title: 'Tablas y cálculos',
          text: 'Ejemplos de presión, caudal, pendientes, diámetros, almacenamiento y selección orientativa de equipos.'
        }, {
          title: 'Procedimientos de instalación',
          text: 'Contenido sobre instalaciones hidráulicas, sanitarias, muebles, bombas, cisternas, tinacos y drenajes.'
        }, {
          title: 'Consulta cuando lo necesites',
          text: 'Una colección organizada para revisar materiales, síntomas, conexiones, cálculos y procesos específicos.'
        }].map((card, i) => <div key={i} className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-103">
              <div className="w-12 h-12 bg-green-50 rounded-xl flex items-center justify-center mb-6" data-id="element-104">
                <CheckCircle2 className="w-6 h-6 text-green-600" data-id="element-105" />
              </div>
              <h3 className="text-xl font-black text-[#0f172a] mb-3" data-id="element-106">
                {card.title}
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-107">{card.text}</p>
            </div>)}
        </div>

        {/* Checklist */}
        <div className="bg-white p-8 md:p-12 rounded-[2.5rem] border border-slate-200 shadow-xl mb-20" data-id="element-108">
          <h3 className="text-2xl font-black text-[#0f172a] mb-10 text-center uppercase tracking-tight" data-id="element-109">
            QUÉ ENCONTRARÁ EL COMPRADOR
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-4" data-id="element-110">
            {['Fundamentos del oficio de la plomería', 'Seguridad, herramientas y preparación del trabajo', 'Interpretación de planos arquitectónicos e isométricos', 'PVC, CPVC, PPR, cobre y tuberías metálicas', 'Técnicas de corte, unión, roscado y termofusión', 'Instalaciones hidráulicas y sistemas de abastecimiento', 'Bombas, cisternas, tinacos y almacenamiento de agua', 'Presión, caudal y pérdidas por fricción', 'Instalaciones sanitarias, drenajes y pendientes', 'Colocación de lavabos, WC, regaderas, tarjas y lavadoras', 'Fundamentos preventivos de instalaciones de gas', 'Calentadores y tecnologías modernas', 'Captación de agua de lluvia y sustentabilidad', 'Diagnóstico de fallas y mantenimiento preventivo', 'Material escrito, recursos visuales y videos prácticos'].map((item, i) => <div key={i} className="flex items-start gap-3" data-id="element-111">
                <Check className="w-5 h-5 text-green-500 shrink-0 mt-0.5" data-id="element-112" />
                <span className="font-bold text-slate-700" data-id="element-113">{item}</span>
              </div>)}
          </div>
        </div>

        {/* Closing */}
        <div className="text-center" data-id="element-114">
          <Button onClick={onAction} className="px-12 py-6 text-xl shadow-2xl shadow-green-500/20" data-id="element-115">
            QUIERO VER TODO EL CONTENIDO
          </Button>
        </div>
      </div>

      {/* Lightbox */}
      <AnimatePresence data-id="element-116">
        {isLightboxOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-[10000] bg-[#0f172a]/95 flex items-center justify-center p-4 md:p-12" onClick={() => setIsLightboxOpen(false)} data-id="element-117">
            <button className="absolute top-6 right-6 text-white/70 hover:text-white transition-colors" onClick={() => setIsLightboxOpen(false)} data-id="element-118">
              <X className="w-10 h-10" data-id="element-119" />
            </button>

            <motion.div initial={{
          scale: 0.9,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.9,
          opacity: 0
        }} className="relative max-w-6xl w-full h-full flex flex-col items-center justify-center" onClick={e => e.stopPropagation()} data-id="element-120">
              <img src={images[currentIndex].url} alt={images[currentIndex].title} className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl" data-id="element-121" />
              <div className="mt-8 text-center text-white" data-id="element-122">
                <h3 className="text-2xl font-black mb-2" data-id="element-123">
                  {images[currentIndex].title}
                </h3>
                <p className="text-white/60 font-bold uppercase tracking-widest" data-id="element-124">
                  {currentIndex + 1} de {images.length}
                </p>
              </div>

              {/* Lightbox Navigation */}
              <button onClick={prev} className="absolute left-0 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 p-4 rounded-full text-white transition-all" data-id="element-125">
                <ChevronLeft className="w-8 h-8" data-id="element-126" />
              </button>
              <button onClick={next} className="absolute right-0 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 p-4 rounded-full text-white transition-all" data-id="element-127">
                <ChevronRight className="w-8 h-8" data-id="element-128" />
              </button>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </section>;
};
const FloatingOfferBar = ({
  onAction
}: {
  onAction: () => void;
}) => {
  const [timeLeft, setTimeLeft] = useState<number | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [currentNotification, setCurrentNotification] = useState(0);
  // --- REAL EVENTS (Nombres reales de personas de Argentina) ---
  const realEvents = [{
    name: 'Facundo',
    location: 'Córdoba',
    action: 'adquirió El Oficio de la Plomería'
  }, {
    name: 'Santiago',
    location: 'Buenos Aires',
    action: 'ingresó a El Oficio de la Plomería'
  }, {
    name: 'Mateo',
    location: 'Rosario',
    action: 'acaba de acceder al programa'
  }, {
    name: 'Valentina',
    location: 'Mendoza',
    action: 'adquirió El Oficio de la Plomería'
  }, {
    name: 'Julián',
    location: 'Salta',
    action: 'acaba de ingresar'
  }, {
    name: 'Lucía',
    location: 'Neuquén',
    action: 'adquirió El Oficio de la Plomería'
  }, {
    name: 'Agustín',
    location: 'Tucumán',
    action: 'acaba de acceder al programa'
  }, {
    name: 'Sofía',
    location: 'Mar del Plata',
    action: 'ingresó a El Oficio de la Plomería'
  }, {
    name: 'Bautista',
    location: 'Santa Fe',
    action: 'adquirió El Oficio de la Plomería'
  }, {
    name: 'Martina',
    location: 'Corrientes',
    action: 'acaba de ingresar'
  }];
  useEffect(() => {
    const handleScroll = () => {
      // Show after 350px scroll
      if (window.scrollY > 350) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    // Timer Logic
    const startTime = localStorage.getItem('plumbing_offer_start');
    const now = Date.now();
    const duration = 15 * 60 * 1000; // 15 minutes
    let initialTime: number;
    if (startTime) {
      const elapsed = now - parseInt(startTime);
      initialTime = Math.max(0, duration - elapsed);
    } else {
      localStorage.setItem('plumbing_offer_start', now.toString());
      initialTime = duration;
    }
    setTimeLeft(Math.floor(initialTime / 1000));
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev === null || prev <= 0) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    // Notification Alternation Logic (Every 15s, show for 4s)
    const notificationInterval = setInterval(() => {
      if (realEvents.length > 0) {
        setShowNotification(true);
        setCurrentNotification(prev => (prev + 1) % realEvents.length);
        setTimeout(() => {
          setShowNotification(false);
        }, 4000);
      }
    }, 15000);
    return () => {
      clearInterval(timer);
      clearInterval(notificationInterval);
    };
  }, []);
  if (isDismissed || timeLeft === null) return null;
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  const isExpired = timeLeft === 0;
  const event = realEvents[currentNotification];
  return <motion.div initial={{
    y: -100
  }} animate={{
    y: isVisible ? 0 : -100
  }} transition={{
    type: 'spring',
    stiffness: 260,
    damping: 20
  }} className="fixed top-0 left-0 right-0 z-[9999] bg-[#0f172a] border-b border-yellow-500/20 shadow-2xl px-4 flex flex-col justify-center h-[75px] md:h-[85px]" data-id="element-129">
      <div className="max-w-7xl mx-auto w-full relative" data-id="element-130">
        {/* Notification Overlay (Alternation) */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: showNotification ? 1 : 0,
        y: showNotification ? 0 : 10
      }} className="absolute inset-0 flex items-center justify-center pointer-events-none z-10" data-id="element-131">
          <span className="text-green-400 font-bold text-sm md:text-base lg:text-lg text-center px-4 bg-[#0f172a] w-full" data-id="element-132">
            {event.name} de {event.location} {event.action}.
          </span>
        </motion.div>

        {/* Main Content (Text + Timer + Button) */}
        <motion.div animate={{
        opacity: showNotification ? 0 : 1
      }} className="flex flex-col md:flex-row items-center justify-center gap-3 md:gap-8" data-id="element-133">
          {/* Desktop: Text */}
          <div className="hidden md:block" data-id="element-134">
            <span className="text-white font-black text-sm lg:text-base uppercase tracking-tight" data-id="element-135">
              {isExpired ? <span className="text-red-500" data-id="element-136">
                  LA OFERTA ESPECIAL HA FINALIZADO
                </span> : '50 % DE DESCUENTO TERMINA EN'}
            </span>
          </div>

          {/* Mobile: Text + Timer */}
          <div className="md:hidden flex flex-col items-center" data-id="element-137">
            <span className="text-white font-bold text-[12px] uppercase tracking-tighter text-center" data-id="element-138">
              {isExpired ? <span className="text-red-500" data-id="element-139">LA OFERTA HA FINALIZADO</span> : <>
                  50 % OFF · TERMINA EN{' '}
                  <span className="text-yellow-400 font-black tabular-nums" data-id="element-140">
                    {formatTime(timeLeft)}
                  </span>
                </>}
            </span>
          </div>

          {/* Desktop: Center (Contador) */}
          {!isExpired && <div className="hidden md:block" data-id="element-141">
              <div className="bg-yellow-400 text-slate-900 px-4 py-1 rounded-lg shadow-inner border-2 border-yellow-500" data-id="element-142">
                <span className="text-2xl lg:text-3xl font-black tabular-nums tracking-tighter" data-id="element-143">
                  {formatTime(timeLeft)}
                </span>
              </div>
            </div>}

          {/* CTA Button */}
          {!isExpired && <button onClick={onAction} className="bg-[#22c55e] hover:bg-[#16a34a] text-white font-black text-[10px] md:text-xs lg:text-sm py-1.5 px-4 md:py-2 md:px-6 rounded-full transition-all transform hover:scale-105 active:scale-95 uppercase tracking-tighter shadow-xl" data-id="element-144">
              APROVECHAR LA OFERTA
            </button>}

          {/* Close Button (Absolute positioned to not break centering) */}
          <button onClick={() => setIsDismissed(true)} className="absolute right-0 top-1/2 -translate-y-1/2 p-1 text-slate-500 hover:text-white transition-colors" aria-label="Cerrar" data-id="element-145">
            <X className="w-4 h-4 md:w-5 md:h-5" data-id="element-146" />
          </button>
        </motion.div>
      </div>
    </motion.div>;
};
// --- Main Page ---
export function PlumbingSalesPage() {
  const checkoutRef = useRef<HTMLDivElement>(null);
  const scrollToCheckout = () => {
    checkoutRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const IMAGES = {
    main: 'https://cdn.magicpatterns.com/uploads/6zx3FkJpo8nZGd2MoYzg6L/el_oficio_de_la_plomeria_SF.png',
    bono1: 'https://cdn.magicpatterns.com/uploads/xuj55RPMfLmvBk6FQbn4wk/bono_1_plomero_D.png',
    bono2: 'https://cdn.magicpatterns.com/uploads/jn8t8dx2EwchSF4CajXYE5/bono_2_plomero_DF.png',
    bono3: 'https://cdn.magicpatterns.com/uploads/wJtXukTp7br4afoY7vRHvr/bono_3_plomero_SF.png',
    bono4: 'https://cdn.magicpatterns.com/uploads/bNh4ZYe6Qs8US6YhZZTpbq/BONO_4_PLOMERO_DF.png',
    completo: 'https://cdn.magicpatterns.com/uploads/eAqdRAJeEiwRt7MfQ8yj5H/PLOMERO_COMPLETO.webp',
    pagoGarantizado: 'https://cdn.magicpatterns.com/uploads/q5HwtgnqeTS6LGTn4xTT2M/zn9vtwfya.png',
    mercadoPagoLogo: 'https://cdn.magicpatterns.com/uploads/7vDM5RPuqA2nqVAZrsrWs6/pago_mercado_libre.jpg',
    mercadoLider: 'https://cdn.magicpatterns.com/uploads/mXb5HEtdcZLHq3QjH1QiUF/MercadoLider-en-Mercado-Libre.webp',
    compraSegura: 'https://cdn.magicpatterns.com/uploads/cesfpi1ZQjAtYp21VMicw4/Helado-Artesanal-1.webp',
    garantiaSello: 'https://cdn.magicpatterns.com/uploads/hxAMZB6ibwXeMc1bEysfwf/safe-purchase-7779878_1280.png'
  };
  return <div className="min-h-screen bg-white text-slate-900 font-sans selection:bg-green-100 selection:text-green-900" data-id="element-147">
      {/* HERO SECTION */}
      <header className="bg-[#0f172a] text-white overflow-hidden pt-12 pb-20 md:pt-24 md:pb-32" data-id="element-148">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-149">
          <div className="flex flex-col md:flex-row items-center gap-12" data-id="element-150">
            {/* Mobile Title & Image (Rule: Show name then image on mobile) */}
            <div className="md:hidden text-center w-full mb-8" data-id="element-151">
              <Badge data-id="element-152">GUÍA PRÁCTICA DE PLOMERÍA MODERNA</Badge>
              <h2 className="text-2xl font-bold mb-6" data-id="element-153">
                EL OFICIO DE LA PLOMERÍA
              </h2>
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} className="relative mx-auto w-full max-w-[280px]" data-id="element-154">
                <img src={IMAGES.main} alt="Ebook El Oficio de la Plomería" className="w-full h-auto drop-shadow-2xl rounded-lg transform rotate-2" data-id="element-155" />
              </motion.div>
            </div>

            {/* Desktop Text Content */}
            <div className="flex-1 text-center md:text-left" data-id="element-156">
              <div className="hidden md:block" data-id="element-157">
                <Badge data-id="element-158">GUÍA PRÁCTICA DE PLOMERÍA MODERNA</Badge>
              </div>
              <h1 className="text-4xl md:text-6xl font-black leading-tight mb-6 tracking-tight" data-id="element-159">
                Aprende el oficio.
                <br data-id="element-160" />
                <span className="text-green-400" data-id="element-161">Deja de improvisar.</span>
              </h1>
              <p className="text-lg md:text-xl text-slate-300 mb-8 max-w-2xl leading-relaxed" data-id="element-162">
                Comprende materiales, conexiones, instalaciones, diagnóstico y
                cálculos mediante un sistema práctico que te ayuda a observar
                cada trabajo con mayor criterio y organización.
              </p>

              <div className="flex flex-col items-center md:items-start gap-4" data-id="element-163">
                <Button onClick={scrollToCheckout} className="w-full md:w-auto" data-id="element-164">
                  SÍ, QUIERO APRENDER PLOMERÍA
                </Button>
                <p className="text-sm text-slate-400 font-medium flex items-center gap-2" data-id="element-165">
                  <CheckCircle2 className="w-4 h-4 text-green-500" data-id="element-166" />
                  Contenido técnico · Consulta visual · Enfoque práctico
                </p>
              </div>
            </div>

            {/* Desktop Image Content (45% block) */}
            <div className="hidden md:block flex-[0.85] relative" data-id="element-167">
              <motion.div initial={{
              opacity: 0,
              x: 50
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              duration: 0.8
            }} className="relative" data-id="element-168">
                <div className="absolute -inset-4 bg-blue-500/10 blur-3xl rounded-full" data-id="element-169"></div>
                <img src={IMAGES.main} alt="Ebook El Oficio de la Plomería" className="relative w-full h-auto drop-shadow-[0_35px_35px_rgba(0,0,0,0.5)] rounded-lg transform rotate-3 hover:rotate-0 transition-transform duration-500" data-id="element-170" />
              </motion.div>
            </div>
          </div>
        </div>
      </header>

      {/* PROBLEMA SECTION */}
      <Section className="bg-slate-50" data-id="element-171">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-172">
          <h2 className="text-3xl md:text-4xl font-black text-slate-900 mb-6" data-id="element-173">
            Una falla pequeña puede convertirse en un problema grande cuando no
            sabes qué revisar.
          </h2>
          <p className="text-lg text-slate-600" data-id="element-174">
            No siempre falta habilidad. Muchas veces falta un proceso claro.
            Cuando no reconoces la pieza, no comprendes el recorrido o no sabes
            cuál puede ser la causa, cualquier reparación comienza a sentirse
            como una apuesta.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-175">
          {[{
          icon: Search,
          text: 'No saber por dónde empezar'
        }, {
          icon: XCircle,
          text: 'Comprar materiales incorrectos'
        }, {
          icon: Droplets,
          text: 'Provocar una fuga'
        }, {
          icon: Calculator,
          text: 'Confundirse con los cálculos'
        }, {
          icon: AlertTriangle,
          text: 'No saber cuánto cobrar'
        }, {
          icon: Hammer,
          text: 'Parecer improvisado frente al cliente'
        }].map((item, i) => <div key={i} className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm flex items-center gap-4 hover:border-blue-200 transition-colors" data-id="element-176">
              <div className="bg-slate-100 p-3 rounded-lg" data-id="element-177">
                <item.icon className="w-6 h-6 text-slate-600" data-id="element-178" />
              </div>
              <span className="font-bold text-slate-800" data-id="element-179">{item.text}</span>
            </div>)}
        </div>
      </Section>

      {/* TRANSFORMACIÓN SECTION */}
      <Section data-id="element-180">
        <div className="text-center mb-16" data-id="element-181">
          <h2 className="text-3xl md:text-4xl font-black text-slate-900 mb-4" data-id="element-182">
            De la improvisación al criterio técnico
          </h2>
          <p className="text-slate-600" data-id="element-183">
            Este sistema te ayuda a comprender el oficio y acompaña tu
            aprendizaje paso a paso.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-184">
          {/* ANTES */}
          <div className="bg-slate-50 p-8 rounded-2xl border-2 border-dashed border-slate-300" data-id="element-185">
            <h3 className="text-xl font-black text-slate-400 uppercase tracking-widest mb-6 flex items-center gap-2" data-id="element-186">
              <XCircle className="w-5 h-5" data-id="element-187" /> ANTES
            </h3>
            <ul className="space-y-4" data-id="element-188">
              {['Información dispersa', 'Dudas frente a cada falla', 'Compra de piezas por prueba y error', 'Inseguridad al presupuestar', 'Trabajos poco organizados'].map((item, i) => <li key={i} className="flex items-start gap-3 text-slate-500" data-id="element-189">
                  <div className="mt-1.5 w-1.5 h-1.5 rounded-full bg-slate-300 shrink-0" data-id="element-190" />
                  {item}
                </li>)}
            </ul>
          </div>

          {/* DESPUÉS */}
          <div className="bg-blue-50 p-8 rounded-2xl border-2 border-blue-200" data-id="element-191">
            <h3 className="text-xl font-black text-blue-600 uppercase tracking-widest mb-6 flex items-center gap-2" data-id="element-192">
              <CheckCircle2 className="w-5 h-5" data-id="element-193" /> DESPUÉS
            </h3>
            <ul className="space-y-4" data-id="element-194">
              {['Conocimientos ordenados', 'Un proceso para observar y revisar', 'Mayor claridad sobre materiales y conexiones', 'Cálculos explicados con ejemplos', 'Presupuestos y entregas más organizados'].map((item, i) => <li key={i} className="flex items-start gap-3 text-slate-800 font-medium" data-id="element-195">
                  <CheckCircle2 className="mt-1 w-4 h-4 text-green-500 shrink-0" data-id="element-196" />
                  {item}
                </li>)}
            </ul>
          </div>
        </div>
      </Section>

      {/* MÉTODO I.D.E.A. */}
      <Section className="bg-[#0f172a] text-white rounded-3xl my-12" data-id="element-197">
        <div className="text-center mb-16" data-id="element-198">
          <Badge data-id="element-199">METODOLOGÍA EXCLUSIVA</Badge>
          <h2 className="text-3xl md:text-5xl font-black mb-4" data-id="element-200">
            El Método I.D.E.A.
          </h2>
          <p className="text-slate-400 max-w-2xl mx-auto" data-id="element-201">
            Un sistema estructurado para abordar cualquier desafío técnico con
            profesionalismo.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-202">
          {[{
          letter: 'I',
          title: 'INTERPRETAR',
          desc: 'Comprende planos, sistemas, materiales y recorridos.',
          icon: Map
        }, {
          letter: 'D',
          title: 'DIAGNOSTICAR',
          desc: 'Identifica síntomas, posibles causas y qué revisar primero.',
          icon: Search
        }, {
          letter: 'E',
          title: 'EJECUTAR',
          desc: 'Selecciona herramientas, conexiones y procedimientos adecuados.',
          icon: Wrench
        }, {
          letter: 'A',
          title: 'ASEGURAR',
          desc: 'Revisa el trabajo, comprueba el funcionamiento y organiza la entrega.',
          icon: ClipboardCheck
        }].map((item, i) => <div key={i} className="relative group" data-id="element-203">
              <div className="text-8xl font-black text-white/5 absolute -top-8 -left-4 group-hover:text-blue-500/10 transition-colors" data-id="element-204">
                {item.letter}
              </div>
              <div className="relative z-10" data-id="element-205">
                <div className="bg-blue-600 w-12 h-12 rounded-lg flex items-center justify-center mb-6 shadow-lg shadow-blue-900/40" data-id="element-206">
                  <item.icon className="w-6 h-6 text-white" data-id="element-207" />
                </div>
                <h3 className="text-xl font-bold mb-3 tracking-tight" data-id="element-208">
                  {item.title}
                </h3>
                <p className="text-slate-400 leading-relaxed" data-id="element-209">{item.desc}</p>
              </div>
            </div>)}
        </div>
      </Section>

      {/* EBOOK PRINCIPAL SECTION */}
      <Section data-id="element-210">
        <div className="flex flex-col lg:flex-row items-center gap-16" data-id="element-211">
          <div className="flex-1 order-2 lg:order-1" data-id="element-212">
            <Badge data-id="element-213">CONTENIDO COMPLETO</Badge>
            <h2 className="text-3xl md:text-4xl font-black text-slate-900 mb-2" data-id="element-214">
              El Oficio de la Plomería
            </h2>
            <p className="text-xl text-slate-500 mb-8 font-medium" data-id="element-215">
              Del conocimiento básico a la práctica profesional
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-8 mb-10" data-id="element-216">
              {['Seguridad y herramientas', 'Interpretación de planos', 'Materiales y conexiones', 'Abastecimiento de agua', 'Bombas, cisternas y tinacos', 'Instalaciones sanitarias', 'Instalación de muebles', 'Fundamentos de gas', 'Sustentabilidad', 'Mantenimiento preventivo'].map((item, i) => <div key={i} className="flex items-center gap-3 text-slate-700" data-id="element-217">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-600" data-id="element-218" />
                  <span className="font-medium" data-id="element-219">{item}</span>
                </div>)}
            </div>

            <Button onClick={scrollToCheckout} data-id="element-220">
              QUIERO APRENDER EL OFICIO
            </Button>
          </div>

          <div className="flex-1 order-1 lg:order-2" data-id="element-221">
            <img src={IMAGES.main} alt="Portada El Oficio de la Plomería" className="w-full max-w-md mx-auto drop-shadow-2xl rounded-lg" data-id="element-222" />
          </div>
        </div>
      </Section>

      {/* BONO PRINCIPAL (Fondo diferente) */}
      <div className="bg-slate-900 text-white py-24" data-id="element-223">
        <Section data-id="element-224">
          <div className="flex flex-col lg:flex-row items-center gap-16" data-id="element-225">
            <div className="flex-1" data-id="element-226">
              <motion.div initial={{
              scale: 0.9,
              opacity: 0
            }} whileInView={{
              scale: 1,
              opacity: 1
            }} viewport={{
              once: true
            }} data-id="element-227">
                <img src={IMAGES.bono1} alt="Bono El Diagnóstico del Plomero" className="w-full max-w-lg mx-auto drop-shadow-[0_0_50px_rgba(34,197,94,0.2)] rounded-lg" data-id="element-228" />
              </motion.div>
            </div>

            <div className="flex-1" data-id="element-229">
              <div className="flex flex-wrap items-center gap-4 mb-6" data-id="element-230">
                <span className="inline-block px-4 py-1 bg-green-500 text-white text-sm font-black uppercase tracking-widest rounded-full" data-id="element-231">
                  BONO PRINCIPAL
                </span>
                <BonusLabel data-id="element-232" />
              </div>
              <h2 className="text-3xl md:text-5xl font-black mb-2" data-id="element-233">
                El Diagnóstico del Plomero
              </h2>
              <p className="text-xl text-green-400 font-bold mb-6" data-id="element-234">
                50 fallas frecuentes y qué revisar primero
              </p>

              <h3 className="text-2xl font-bold mb-6 text-slate-100 italic" data-id="element-235">
                “No empieces cambiando piezas. Empieza diagnosticando.”
              </h3>

              <p className="text-slate-400 text-lg mb-8 leading-relaxed" data-id="element-236">
                Una guía de consulta para reconocer síntomas, evaluar causas
                probables y seguir un orden de revisión frente a fugas,
                obstrucciones, baja presión y fallas comunes.
              </p>

              <div className="bg-white/5 border border-white/10 p-6 rounded-xl mb-8" data-id="element-237">
                <p className="text-slate-200 font-medium italic" data-id="element-238">
                  “El ebook te ayuda a comprender el oficio. Este bono te ayuda
                  a pensar frente a una falla.”
                </p>
              </div>

              <p className="text-sm text-slate-500 font-bold italic" data-id="element-239">
                “Disponible sin costo adicional al adquirir hoy El Oficio de la
                Plomería.”
              </p>
            </div>
          </div>
        </Section>
      </div>

      {/* OTROS BONOS */}
      <Section data-id="element-240">
        <div className="text-center mb-16" data-id="element-241">
          <h2 className="text-3xl md:text-5xl font-black text-slate-900 mb-4" data-id="element-242">
            HOY NO SOLO TE LLEVAS EL EBOOK PRINCIPAL
          </h2>
          <p className="text-xl text-slate-600 max-w-4xl mx-auto" data-id="element-243">
            También recibes cuatro recursos complementarios para diagnosticar
            fallas, reconocer materiales, simplificar cálculos y organizar tus
            trabajos con mayor profesionalismo.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-12" data-id="element-244">
          {[{
          id: 2,
          title: 'Materiales y Conexiones del Plomero',
          desc: 'Guía visual para identificar tuberías, piezas, herramientas y accesorios.',
          image: IMAGES.bono2,
          benefits: ['Identificación rápida', 'Ahorro en compras', 'Catálogo visual']
        }, {
          id: 3,
          title: 'Los Cálculos del Plomero',
          desc: 'Tablas, fórmulas y ejemplos prácticos para dimensionar correctamente.',
          image: IMAGES.bono3,
          benefits: ['Fórmulas simplificadas', 'Tablas de referencia', 'Ejemplos reales']
        }, {
          id: 4,
          title: 'Gestión Profesional para Plomeros',
          desc: 'Plantillas para inspeccionar, presupuestar, organizar y entregar trabajos.',
          image: IMAGES.bono4,
          benefits: ['Presupuestos claros', 'Control de tiempos', 'Imagen profesional']
        }].map((bono, i) => <div key={i} className="flex flex-col bg-slate-50 p-6 rounded-2xl border border-slate-200" data-id="element-245">
              <div className="mb-6 h-64 flex items-center justify-center relative" data-id="element-246">
                <img src={bono.image} alt={bono.title} className="max-h-full w-auto drop-shadow-xl rounded-lg" data-id="element-247" />
                <div className="absolute -top-4 -right-2" data-id="element-248">
                  <BonusLabel data-id="element-249" />
                </div>
              </div>
              <Badge data-id="element-250">BONO {bono.id}</Badge>
              <h3 className="text-xl font-black mb-3 text-slate-900 leading-tight" data-id="element-251">
                {bono.title}
              </h3>
              <p className="text-slate-600 mb-6 flex-grow" data-id="element-252">{bono.desc}</p>
              <ul className="space-y-2 mb-6" data-id="element-253">
                {bono.benefits.map((b, idx) => <li key={idx} className="flex items-center gap-2 text-sm font-bold text-slate-800" data-id="element-254">
                    <CheckCircle2 className="w-4 h-4 text-green-500" data-id="element-255" />
                    {b}
                  </li>)}
              </ul>
              <p className="text-xs text-slate-400 font-bold italic border-t border-slate-200 pt-4" data-id="element-256">
                “Disponible sin costo adicional al adquirir hoy El Oficio de la
                Plomería.”
              </p>
            </div>)}
        </div>

        <div className="mt-20 flex flex-col items-center gap-8" data-id="element-257">
          <div className="bg-[#0f172a] text-white px-12 py-4 rounded-full font-black text-xl md:text-2xl tracking-tight shadow-xl border-2 border-yellow-500/30" data-id="element-258">
            LOS 4 BONOS ESTÁN INCLUIDOS{' '}
            <span className="text-yellow-400" data-id="element-259">GRATIS HOY</span>
          </div>
        </div>
      </Section>

      {/* PARA QUIÉN ES */}
      <Section className="bg-slate-50 rounded-3xl" data-id="element-260">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16" data-id="element-261">
          <div data-id="element-262">
            <h2 className="text-3xl font-black text-slate-900 mb-8" data-id="element-263">
              Creado para quienes quieren aprender el oficio con orden
            </h2>
            <div className="space-y-4" data-id="element-264">
              {['Principiantes', 'Ayudantes de plomería', 'Trabajadores de mantenimiento', 'Personas que ya realizan reparaciones básicas', 'Técnicos que necesitan ordenar conocimientos', 'Personas que quieren comprender mejor materiales, fallas y procedimientos'].map((item, i) => <div key={i} className="flex items-center gap-4 bg-white p-4 rounded-xl shadow-sm border border-slate-200" data-id="element-265">
                  <div className="bg-blue-100 p-2 rounded-full" data-id="element-266">
                    <CheckCircle2 className="w-5 h-5 text-blue-600" data-id="element-267" />
                  </div>
                  <span className="font-bold text-slate-700" data-id="element-268">{item}</span>
                </div>)}
            </div>
          </div>

          <div className="bg-white p-8 rounded-2xl border border-red-100 shadow-sm" data-id="element-269">
            <h3 className="text-2xl font-black text-red-600 mb-6 flex items-center gap-2" data-id="element-270">
              <ShieldAlert className="w-6 h-6" data-id="element-271" /> No es para ti si:
            </h3>
            <ul className="space-y-6" data-id="element-272">
              {['Buscas resultados instantáneos sin practicar.', 'Quieres improvisar trabajos peligrosos.', 'Esperas que un ebook reemplace habilitaciones o certificaciones.', 'No estás dispuesto a respetar medidas de seguridad.'].map((item, i) => <li key={i} className="flex items-start gap-4 text-slate-600" data-id="element-273">
                  <XCircle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" data-id="element-274" />
                  <span className="font-medium" data-id="element-275">{item}</span>
                </li>)}
            </ul>
          </div>
        </div>
      </Section>

      {/* SEGURIDAD Y RESPONSABILIDAD */}
      <Section data-id="element-276">
        <div className="bg-blue-900 text-white p-8 md:p-12 rounded-3xl flex flex-col md:flex-row items-center gap-8 border-4 border-blue-800" data-id="element-277">
          <div className="bg-blue-800 p-6 rounded-2xl" data-id="element-278">
            <ShieldAlert className="w-12 h-12 text-blue-300" data-id="element-279" />
          </div>
          <div data-id="element-280">
            <h2 className="text-2xl font-black mb-4" data-id="element-281">
              El conocimiento también consiste en saber cuándo detenerse
            </h2>
            <p className="text-blue-100 leading-relaxed max-w-3xl" data-id="element-282">
              El contenido relacionado con gas y trabajos regulados tiene un
              propósito educativo y preventivo. Toda intervención debe cumplir
              con la normativa local y ser realizada o supervisada por personal
              habilitado cuando corresponda.
            </p>
          </div>
        </div>
      </Section>

      {/* FAQ SECTION */}
      <Section data-id="element-283">
        <div className="text-center mb-16" data-id="element-284">
          <h2 className="text-3xl md:text-4xl font-black text-slate-900 mb-4" data-id="element-285">
            Preguntas Frecuentes
          </h2>
          <p className="text-slate-600" data-id="element-286">
            Resolvemos tus dudas sobre el material de estudio.
          </p>
        </div>

        <div className="max-w-3xl mx-auto" data-id="element-287">
          <FAQItem question="¿Puedo utilizarlo si empiezo desde cero?" answer="Sí, el material está diseñado para llevarte desde los conceptos más básicos de materiales y herramientas hasta procedimientos más complejos, siempre con un enfoque práctico y visual." data-id="element-288" />
          <FAQItem question="¿Sirve si ya realizo algunos trabajos?" answer="Absolutamente. Muchos técnicos utilizan este material para ordenar sus conocimientos, mejorar sus procesos de diagnóstico y profesionalizar la gestión de sus presupuestos y entregas." data-id="element-289" />
          <FAQItem question="¿Necesito comprar todas las herramientas?" answer="No. El ebook te explica cuáles son las herramientas esenciales y cuáles son para trabajos específicos, permitiéndote armar tu equipo de forma inteligente y progresiva." data-id="element-290" />
          <FAQItem question="¿Los cálculos están explicados con ejemplos?" answer="Sí, evitamos la teoría compleja. Los cálculos de presión, caudal y dimensionamiento se presentan con tablas de referencia y ejemplos de casos reales que encontrarás en el día a día." data-id="element-291" />
          <FAQItem question="¿Puedo consultar los ebooks desde el teléfono?" answer="Sí, los archivos están en formato digital optimizado para que puedas consultarlos desde tu smartphone, tablet o computadora en cualquier momento, incluso mientras estás en una obra." data-id="element-292" />
          <FAQItem question="¿El material reemplaza la práctica?" answer="No. La plomería es un oficio práctico. Este material es una guía técnica que te da el criterio para que tu práctica sea segura, organizada y profesional, pero la ejecución real es fundamental." data-id="element-293" />
          <FAQItem question="¿Incluye información sobre gas?" answer="Incluye fundamentos esenciales y medidas de seguridad preventivas. Sin embargo, recordamos que las instalaciones de gas suelen estar reguladas y deben ser realizadas por personal certificado." data-id="element-294" />
          <FAQItem question="¿Cómo recibiré los archivos?" answer="vas a recibir un mail con todo el acceso al contenido, entrega inmediata en formato pdf + los videos educativos." data-id="element-295" />
        </div>
      </Section>

      {/* FINAL SECTION / CHECKOUT */}
      <footer ref={checkoutRef} className="bg-slate-50 border-t border-slate-200 pt-24 pb-12" data-id="element-296">
        <Section data-id="element-297">
          <div className="text-center mb-12 md:mb-20" data-id="element-298">
            <h2 className="text-3xl md:text-5xl font-black text-slate-900 mb-6" data-id="element-299">
              MIRA TODO LO QUE RECIBES HOY
            </h2>
            <p className="text-lg md:text-xl text-slate-600 max-w-4xl mx-auto leading-relaxed" data-id="element-300">
              Obtienes el ebook principal{' '}
              <strong data-id="element-301">El Oficio de la Plomería</strong> junto con cuatro
              recursos complementarios para diagnosticar fallas, identificar
              materiales, simplificar cálculos y organizar tus trabajos.
            </p>
          </div>

          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20 bg-white p-6 md:p-12 rounded-[2.5rem] shadow-2xl border border-slate-200 overflow-hidden" data-id="element-302">
            {/* Image Section */}
            <div className="flex-1 w-full" data-id="element-303">
              <img src={IMAGES.completo} alt="Paquete Completo El Oficio de la Plomería" className="w-full h-auto rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-500" data-id="element-304" />
            </div>

            {/* Offer Details Section */}
            <div className="flex-1 w-full" data-id="element-305">
              <div className="space-y-8" data-id="element-306">
                <div className="space-y-4" data-id="element-307">
                  {['Ebook principal: El Oficio de la Plomería', 'El Diagnóstico del Plomero', 'Materiales y Conexiones del Plomero', 'Los Cálculos del Plomero', 'Gestión Profesional para Plomeros'].map((item, i) => <div key={i} className="flex items-center gap-3 text-slate-800 font-bold text-lg" data-id="element-308">
                      <CheckCircle2 className="w-6 h-6 text-green-500 shrink-0" data-id="element-309" />
                      <span data-id="element-310">{item}</span>
                    </div>)}
                </div>

                <div className="bg-[#0f172a] text-white px-6 py-3 rounded-lg font-black text-center text-lg tracking-wide border-2 border-yellow-500/30" data-id="element-311">
                  LOS 4 BONOS ESTÁN INCLUIDOS{' '}
                  <span className="text-yellow-400" data-id="element-312">GRATIS HOY</span>
                </div>

                <div className="pt-4 border-t border-slate-100" data-id="element-313">
                  <p className="text-slate-500 font-bold uppercase tracking-widest text-sm mb-1" data-id="element-314">
                    PRECIO ESPECIAL DE LANZAMIENTO
                  </p>
                  <div className="flex items-baseline gap-2" data-id="element-315">
                    <span className="text-5xl font-black text-slate-900" data-id="element-316">
                      $19.990
                    </span>
                    <span className="text-slate-400 font-medium line-through" data-id="element-317">
                      $39.990
                    </span>
                  </div>
                  <p className="text-green-600 font-bold text-sm mt-1" data-id="element-318">
                    3 CUOTAS SIN INTERÉS
                  </p>
                </div>

                <div className="space-y-4" data-id="element-319">
                  <div className="flex flex-col items-center" data-id="element-320">
                    <motion.div initial={{
                    scale: 0.9,
                    opacity: 0
                  }} whileInView={{
                    scale: 1,
                    opacity: 1
                  }} animate={{
                    y: [0, -5, 0]
                  }} transition={{
                    y: {
                      duration: 2,
                      repeat: Infinity,
                      ease: 'easeInOut'
                    }
                  }} className="bg-red-600 text-white px-4 py-1 rounded-full text-sm font-black mb-2 shadow-lg" data-id="element-321">
                      50% OFF
                    </motion.div>
                    <Button onClick={scrollToCheckout} className="w-full py-6 text-xl md:text-2xl shadow-xl shadow-green-500/20" data-id="element-322">
                      QUIERO APRENDER EL OFICIO
                    </Button>
                  </div>
                  <p className="text-slate-500 text-center text-sm font-bold italic" data-id="element-323">
                    “Recibirás el ebook principal y todos los bonos incluidos en
                    esta oferta.”
                  </p>

                  {/* Trust and Payment Section */}
                  <div className="pt-10 flex flex-col items-center space-y-10 w-full" data-id="element-324">
                    {/* 2. MÉTODOS DE PAGO DISPONIBLES */}
                    <div className="w-full text-center" data-id="element-325">
                      <h3 className="text-lg md:text-xl font-black text-slate-900 mb-4 uppercase tracking-tight" data-id="element-326">
                        MÉTODOS DE PAGO DISPONIBLES
                      </h3>
                      <div className="max-w-[600px] mx-auto w-full px-4 md:px-0" data-id="element-327">
                        <img src={IMAGES.pagoGarantizado} alt="Visa, Mastercard, American Express, Mercado Pago" className="w-full h-auto mx-auto" data-id="element-328" />
                      </div>
                    </div>

                    {/* 3. Pago procesado por Mercado Pago */}
                    <div className="w-full text-center" data-id="element-329">
                      <img src={IMAGES.mercadoPagoLogo} alt="Mercado Pago" className="w-[120px] md:w-[160px] h-auto mx-auto mb-3" data-id="element-330" />
                      <p className="text-xs md:text-sm text-slate-500 font-bold" data-id="element-331">
                        Pago procesado de forma segura a través de Mercado Pago.
                      </p>
                    </div>

                    {/* 4. Compra Segura */}
                    <div className="w-full text-center" data-id="element-332">
                      <img src={IMAGES.compraSegura} alt="Compra Segura" className="w-[150px] md:w-[230px] h-auto mx-auto mb-2" data-id="element-333" />
                      <p className="text-xs md:text-sm text-slate-500 font-medium" data-id="element-334">
                        Tu pago se procesa mediante una plataforma segura.
                      </p>
                    </div>

                    {/* 5. Reputación del Vendedor */}
                    <div className="w-full text-center" data-id="element-335">
                      <div className="max-w-md mx-auto" data-id="element-336">
                        <img src={IMAGES.mercadoLider} alt="Reputación MercadoLíder Platinum" className="w-full h-auto mx-auto" data-id="element-337" />
                      </div>
                    </div>

                    {/* 6. Garantía de 7 Días (Pequeña y al final) */}
                    <div className="w-full text-center pt-8" data-id="element-338">
                      <p className="text-[10px] md:text-xs text-slate-400 font-bold uppercase tracking-widest mb-4" data-id="element-339">
                        Compra con tranquilidad
                      </p>
                      <img src={IMAGES.garantiaSello} alt="Garantía 7 Días Incondicional" className="w-[110px] md:w-[170px] h-auto mx-auto mb-4" data-id="element-340" />
                      <p className="text-[10px] md:text-xs text-slate-500 font-medium max-w-[300px] mx-auto leading-relaxed" data-id="element-341">
                        Tu compra cuenta con una garantía de 7 días según las
                        condiciones informadas.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* REVIEWS AND SOCIAL PROOF SECTION */}
          <ReviewsSection data-id="element-342" />

          {/* MIRA LO QUE ENCONTRARÁS ADENTRO */}
          <InsideContentSection onAction={scrollToCheckout} data-id="element-343" />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-slate-500 text-sm font-medium border-t border-slate-200 pt-16 mt-20" data-id="element-344">
            <div data-id="element-345">
              <p className="uppercase tracking-widest text-slate-400 mb-2" data-id="element-346">
                AUTOR
              </p>
              <p className="text-slate-900 font-bold" data-id="element-347">Mundo de Oficios</p>
            </div>
            <div data-id="element-348">
              <p className="uppercase tracking-widest text-slate-400 mb-2" data-id="element-349">
                GARANTÍA
              </p>
              <p className="text-slate-900 font-bold" data-id="element-350">de 7 DIAS</p>
            </div>
            <div data-id="element-351">
              <p className="uppercase tracking-widest text-slate-400 mb-2" data-id="element-352">
                CONTACTO
              </p>
              <p className="text-slate-900 font-bold" data-id="element-353">
                soporte.primexdigital@gmail.com
              </p>
            </div>
          </div>

          <div className="mt-20 text-slate-400 text-xs" data-id="element-354">
            © {new Date().getFullYear()} El Oficio de la Plomería. Todos los
            derechos reservados.
          </div>
        </Section>
      </footer>

      {/* FLOATING OFFER BAR */}
      <FloatingOfferBar onAction={scrollToCheckout} data-id="element-355" />
    </div>;
}